import { createContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuthStore } from '../store/AuthStore';

/**
 * FeaturesContext - Provides organization feature access throughout the app
 *
 * This context fetches enabled features once when mounted and provides
 * the data to all child components. Single API call, shared state.
 */
export const FeaturesContext = createContext(undefined);

/**
 * FeaturesProvider - Fetches and provides feature access data
 *
 * Wrap your app with this provider to enable feature checking.
 * Typically wrapped around MainLayout or App root.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
const FEATURES_CACHE_PREFIX = 'govify_features_';

const readCachedFeatures = (orgId) => {
  try {
    const raw = localStorage.getItem(FEATURES_CACHE_PREFIX + (orgId || ''));
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore corrupt cache */ }
  return new Set();
};

const writeCachedFeatures = (orgId, featureSet) => {
  try {
    localStorage.setItem(FEATURES_CACHE_PREFIX + (orgId || ''), JSON.stringify([...featureSet]));
  } catch { /* ignore quota errors */ }
};

export const FeaturesProvider = ({ children }) => {
  const { user } = useAuthStore();
  const orgId = user?.organizationId;
  const [features, setFeatures] = useState(() => readCachedFeatures(orgId));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch enabled features for the user's organization
  // When org changes (e.g. different login), load that org's cache immediately
  useEffect(() => {
    setFeatures(readCachedFeatures(orgId));
  }, [orgId]);

  const fetchFeatures = useCallback(async () => {
    if (!orgId) {
      setIsLoading(false);
      // Keep cached features — don't wipe them during transient auth blips
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get('/marketplace/features');

      const enabledFeatures = response.data?.enabledFeatures || [];

      const featureSet = new Set(enabledFeatures);
      setFeatures(featureSet);
      writeCachedFeatures(orgId, featureSet);
    } catch (err) {
      console.error('Failed to fetch enabled features:', err);
      setError(err);
      // On error, keep existing features (cached) instead of wiping to empty.
      // This prevents transient API failures from hiding the entire sidebar.
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // Fetch features on mount and when organizationId changes
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  /**
   * Check if a specific feature is enabled
   * @param {string} featureType - Feature type to check (e.g., 'messages', 'documents')
   * @returns {boolean} - True if feature is enabled, false otherwise
   */
  const hasFeature = useCallback(
    (featureType) => {
      return features.has(featureType);
    },
    [features],
  );

  /**
   * Check if ALL specified features are enabled
   * @param {...string} featureTypes - Feature types to check
   * @returns {boolean} - True if all features are enabled, false otherwise
   */
  const hasAllFeatures = useCallback(
    (...featureTypes) => {
      return featureTypes.every((feature) => features.has(feature));
    },
    [features],
  );

  /**
   * Check if ANY of the specified features are enabled
   * @param {...string} featureTypes - Feature types to check
   * @returns {boolean} - True if any feature is enabled, false otherwise
   */
  const hasAnyFeature = useCallback(
    (...featureTypes) => {
      return featureTypes.some((feature) => features.has(feature));
    },
    [features],
  );

  const value = {
    features, // Set of enabled feature types
    isLoading, // Loading state
    error, // Error if fetch failed
    hasFeature, // Function to check single feature
    hasAllFeatures, // Function to check multiple features (AND)
    hasAnyFeature, // Function to check multiple features (OR)
    refetch: fetchFeatures, // Function to manually refetch features
  };

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};

export default FeaturesContext;
